// Internal
import { select, selectAll, loadReCaptcha, initTabbedForms } from './simplerToolkit.js';

// External


document.addEventListener('DOMContentLoaded', () => {

    const TABLET_LANDSCAPE = 1067;

    // Unified Floating Sidebar / Social Toggle
    (function initFloatingSidebar() {
        const toggleBtn = document.querySelector('.social-toggle');
        const wrapper = document.querySelector('.floating-sidebar-wrapper');

        if (!toggleBtn || !wrapper) return;

        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            wrapper.classList.toggle('open');
        });
    })();

    // Ajax Form Submissions
    (function initAjaxForms() {
        const forms = document.querySelectorAll('form[data-action-ajax]');
        if (!forms.length) return;

        const clearErrors = (form) => {
            form.querySelectorAll('.field-error, .form-error, .input-error').forEach(n => n.remove());
            form.querySelectorAll('.has_error, .error').forEach(el => el.classList.remove('has_error', 'error'));
            form.querySelectorAll('[aria-invalid="true"]').forEach(el => el.setAttribute('aria-invalid', 'false'));
        };

        const showErrors = (form, errors = {}) => {
            // Top-level summary for non-field errors
            const summary = document.createElement('div');
            summary.className = 'form-error';
            const generalErrors = [];

            Object.entries(errors || {}).forEach(([field, messages]) => {
                const msgs = Array.isArray(messages) ? messages : [String(messages)];
                // Try to find input by name
                const input = form.querySelector(`[name="${CSS.escape(field)}"]`);
                if (input && input.type !== 'hidden') {
                    const err = document.createElement('div');
                    err.className = 'field-error';
                    err.textContent = msgs[0];
                    // mark container if possible
                    const container = input.closest('.form_group, .form-group, .field, .input, p, div');
                    (container || input).classList.add('has_error');
                    input.setAttribute('aria-invalid', 'true');
                    // insert error after the input
                    (input.parentElement || container || form).appendChild(err);
                } else {
                    generalErrors.push(...msgs);
                }
            });

            if (generalErrors.length) {
                const g = document.createElement('div');
                g.className = 'form-error';
                g.innerHTML = generalErrors.map(m => `<p>${m}</p>`).join('');
                form.prepend(g);
            }

            // Focus first invalid input
            const firstInvalid = form.querySelector('[aria-invalid="true"]');
            if (firstInvalid) firstInvalid.focus({preventScroll: false});
        };

        const showSuccess = (form, payload = {}) => {
            const title = payload.title || form.getAttribute('data-success-title') || 'Thank you';
            const message = payload.message || form.getAttribute('data-success-message') || 'Your submission has been received.';
            const html = payload.html;

            const wrapper = document.createElement('div');
            wrapper.className = 'form-success';
            wrapper.innerHTML = html || `<h3>${title}</h3>${message}`;

            form.replaceWith(wrapper);
        };

        const performAjax = async (form) => {
            clearErrors(form);

            // Disable submit button
            const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
            const prevText = submitBtn && (submitBtn.value || submitBtn.textContent);
            if (submitBtn) {
                submitBtn.disabled = true;
                if ('value' in submitBtn && submitBtn.value) submitBtn.value = 'Please wait...';
                else submitBtn.textContent = 'Please wait...';
            }

            try {
                const url = form.getAttribute('action') || window.location.href;
                let method = (form.getAttribute('method') || 'GET').toUpperCase();

                const formData = new FormData(form);
                // method spoofing via _method
                const spoof = formData.get('_method');
                if (spoof) method = String(spoof).toUpperCase();

                // Google Tracking
                if(typeof window.dataLayer !== 'undefined') {
                    dataLayer.push({
                        event: 'form_submission'
                    });
                }

                let fetchUrl = url;
                let fetchOpts = {
                    method,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'same-origin'
                };

                if (method === 'GET' || method === 'HEAD') {
                    const qs = new URLSearchParams(formData).toString();
                    fetchUrl = qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
                } else {
                    fetchOpts.body = formData; // includes _token
                    // CSRF header fallback
                    const token = select('meta[name="csrf-token"]')?.getAttribute('content');
                    if (token) (fetchOpts.headers)['X-CSRF-TOKEN'] = token;
                }

                const res = await fetch(fetchUrl, fetchOpts);
                const isJson = (res.headers.get('content-type') || '').includes('application/json');
                let data = null;
                try {
                    data = isJson ? await res.json() : null;
                } catch {
                    data = null;
                }

                if (!res.ok) {
                    // Laravel may send 422 with errors
                    const errs = data && data.errors ? data.errors : {general: ['We couldn\u2019t submit your form due to a server error. Please try again, or contact us if the problem persists.']};
                    showErrors(form, errs);
                    return;
                }

                // Success path; handle conventional shapes
                if (data && (data.success || data.status === 'ok')) {
                    if (data.redirect) {
                        window.location.href = data.redirect;
                        return;
                    }
                    showSuccess(form, {title: data.data.title, message: data.data.message});
                } else if (data && data.errors) {
                    showErrors(form, data.errors);
                } else {
                    // Fallback: treat as success
                    showSuccess(form, {});
                }

            } catch (err) {
                console.error('[Form AJAX] Submission error:', err);
                showErrors(form, {general: ['An unexpected error occurred. Please try again.']});
            } finally {
                const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
                if (submitBtn) {
                    submitBtn.disabled = false;
                    if (prevText) {
                        if ('value' in submitBtn && submitBtn.value) submitBtn.value = prevText;
                        else submitBtn.textContent = prevText;
                    }
                }
            }
        };

        // Attach listener
        forms.forEach(form => {
            form.addEventListener('submit', async (e) => {
                e.preventDefault();
                e.stopPropagation();

                form.querySelector('input[type="submit"], button[type="submit"]')?.setAttribute('disabled', 'true');

                // If form declares data-captcha, execute reCAPTCHA first, then continue
                if (form.hasAttribute('data-captcha')) {
                    const siteKeyMeta = document.querySelector('meta[name="recaptcha-site-key"]');
                    const siteKey = siteKeyMeta?.getAttribute('content');
                    const ensureToken = () => new Promise((resolve, reject) => {
                        if (!window.grecaptcha || !siteKey) return reject(new Error('reCAPTCHA not configured'));
                        grecaptcha.ready(() => {
                            grecaptcha.execute(siteKey, {action: 'submit'})
                                .then(token => {
                                    let input = form.querySelector('input[name="g-recaptcha-response"]');
                                    if (!input) {
                                        input = document.createElement('input');
                                        input.type = 'hidden';
                                        input.name = 'g-recaptcha-response';
                                        form.appendChild(input);
                                    }
                                    input.value = token;
                                    resolve();
                                })
                                .catch(reject);
                        });
                    });

                    // Try run; if grecaptcha missing, fall back to loader below if available
                    try {
                        await ensureToken();
                        await performAjax(form);
                    } catch (err) {
                        // Try to load and retry if loadReCaptcha exists (defined elsewhere in this file)
                        if (typeof loadReCaptcha === 'function') {
                            await new Promise(res => loadReCaptcha(async () => {
                                try {
                                    await ensureToken();
                                } catch {
                                }
                                res();
                            }));
                            await performAjax(form);
                        } else {
                            console.warn('[Form AJAX] reCAPTCHA not available; continuing without token.');
                            await performAjax(form);
                        }
                    }
                } else {
                    await performAjax(form);
                }
            });
        });
    })();

    // Init tabbed forms
    initTabbedForms();


    /**
     * Global Click Handler
     */

    document.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target.parentElement;

        // Action - Open Pop Out Menu
        if (target.closest('[data-pop-out-menu-toggle]')) {
            event.preventDefault();
            window.togglePopOutMenu(target.closest('[data-pop-out-menu-toggle]').getAttribute('data-pop-out-menu-toggle'));
            return;
        }

        // Section tab
        if(target.closest('[action-display-tab]')){
            event.preventDefault();

            // Close all other tabs within section
            let section = target.closest('section');
            section.querySelectorAll('[data-tab]').forEach(tab => {
                if(tab !== target.closest('[data-tab]')){
                    hideTab(tab.getAttribute('data-tab'));
                }
            })

            // Show tab
            displayTab(target.closest('[action-display-tab]').getAttribute('action-display-tab'));

        }

        // Action - Close Pop Out Menu / Overlay
        if (target.classList.contains('pop_out_menu_close') || target.closest('.pop_out_menu_overlay')) {
            const popout = target.closest('.pop_out_menu') || document.querySelector('.pop_out_menu.active');
            if (popout) {
                event.preventDefault();
                window.togglePopOutMenu(popout.getAttribute('data-pop-out-menu-id'), 'close');

                // Reset navigation state if it's the hamburger menu
                if (popout.classList.contains('site_hamburger_navigation')) {
                    const sections = popout.querySelectorAll('.pop_out_menu_section');
                    sections.forEach(s => {
                        s.style.display = s.classList.contains('main_mobile_nav') ? 'block' : 'none';
                        s.classList.toggle('active', s.classList.contains('main_mobile_nav'));
                    });
                    const backBtn = popout.querySelector('.back_button');
                    if (backBtn) backBtn.style.display = 'none';
                    // const logo = popout.querySelector('.mobile_logo');
                    // if (logo) logo.style.display = 'inline-block';
                }
            }
        }

        // Mobile Navigation Multi-level handling
        if (target.closest('.site_hamburger_navigation .parent_link')) {
            event.preventDefault();
            const link = target.closest('.parent_link');
            const menuId = link.getAttribute('data-menu-target');
            const popout = link.closest('.pop_out_menu');
            const targetMenu = popout.querySelector(`#${menuId}`);
            const mainMenu = popout.querySelector('.main_mobile_nav');
            const backBtn = popout.querySelector('.back_button');
            const contactBtn = popout.querySelector('.contact_button');

            if (targetMenu && mainMenu) {
                mainMenu.style.display = 'none';
                mainMenu.classList.remove('active');
                targetMenu.style.display = 'block';
                targetMenu.classList.add('active');
                if (backBtn) backBtn.style.display = 'flex';
                if (contactBtn) contactBtn.style.display = 'none';
                popout.scrollTop = 0;
            }
            return;
        }

        if (target.closest('.site_hamburger_navigation .back_button')) {
            event.preventDefault();
            const backBtn = target.closest('.back_button');
            const popout = backBtn.closest('.pop_out_menu');
            const activeMenu = popout.querySelector('.pop_out_menu_section.active');
            const mainMenu = popout.querySelector('.main_mobile_nav');
            const contactBtn = popout.querySelector('.contact_button');

            if (activeMenu && mainMenu) {
                activeMenu.style.display = 'none';
                activeMenu.classList.remove('active');
                mainMenu.style.display = 'block';
                mainMenu.classList.add('active');
                backBtn.style.display = 'none';
                if (contactBtn) contactBtn.style.display = 'flex';
                popout.scrollTop = 0;
            }
            return;
        }

        if (target.closest('.footer-heading') && isMobile) {
            target.closest('.nav_column').classList.toggle('show');
        }

        if (target.closest('.accordion-item--title')) {
            const section = target.closest('section');
            const currentItem = target.closest('.accordion-item');

            section.querySelectorAll('.accordion-item').forEach(item => {
                if (item !== currentItem) {
                    item.classList.remove('show');
                }
            });

            currentItem.classList.toggle('show');
        }
    });

    // Initialize Embedded Maps with Google Maps
    initEmbeddedMaps();

    // Lazy load images on site
    selectAll('img[loading="lazy"]').forEach(image => {
        fadeLazyLoadImage(image);
    });
    selectAll('picture').forEach(picture => {
        initPictureFade(picture);
    });
    observeLazyImages();

    // Navigation Hover Class
    const header = select('#page_header');
    const navItemsWithChildren = selectAll('.desktop_nav > ul > li.has_children');
    let navHoverTimeout;

    navItemsWithChildren.forEach(item => {
        item.addEventListener('mouseenter', () => {
            if (window.innerWidth >= TABLET_LANDSCAPE) {
                clearTimeout(navHoverTimeout);
                header.classList.add('navigation_hover');
            }
        });
        item.addEventListener('mouseleave', (e) => {
            if (window.innerWidth >= TABLET_LANDSCAPE) {
                // Wait 500ms before removing the class to allow for movement into the dropdown
                navHoverTimeout = setTimeout(() => {
                    header.classList.remove('navigation_hover');
                }, 500);
            }
        });
    });

    // Carousel initialisers moved to dedicated modules under resources/js/carousels/*

    // Wrap tables for horizontal scroll on smaller screens
    (function initTableScrollWrappers() {
        document.querySelectorAll('table').forEach((table) => {
            if (table.parentElement?.classList.contains('scrollwrapper')) return;

            const wrapper = document.createElement('div');
            wrapper.classList.add('scrollwrapper');

            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);
        });
    })();
});
